import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Box, Card, CardHeader, CardContent, CardActions, CardMedia, IconButton, Avatar, Button  } from '@mui/material';
import Grid from '@mui/material/Grid2';
import GitHubIcon from '@mui/icons-material/GitHub';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SkillIcon from '@mui/icons-material/Build';
import LaptopMacIcon from '@mui/icons-material/LaptopMac';
import { useMediaQuery } from '@mui/material';
import { useTheme, Theme } from '@mui/material/styles';
import DescriptionIcon from '@mui/icons-material/Description';
import EastSharpIcon from '@mui/icons-material/EastSharp';
import WestSharpIcon from '@mui/icons-material/WestSharp';
import { buttonStyles } from '../utils/utils';

const academicProjects = [
  {
    title: 'SAMS',
    year: '2024',
    image: 'face-recognition.png',
    description:
      'SAMS, or Smart Attendance Management System, is a project that automates attendance using facial recognition and a webcam. It features two dashboards: one for the admin and one for the student. The admin dashboard allows for student attendance marking via webcam, enrolling students in new courses, and viewing attendance history logs. The student dashboard displays enrolled courses and individual attendance history.',
    technologies: ['TypeScript', 'Next JS', 'FastAPI', 'Mongo DB', 'Jest', 'Heroku', 'Vercel', 'GitHub'],
    githubLink: 'https://github.com/Anjali0407-git/SAMS-facial-recognition',
    // websiteLink: 'https://sams-facial-recognition-alpha.vercel.app/'
  },
  {
    title: 'Public Speaking Practice App',
    year: '2024',
    image: 'public-speaking.png',
    description:
      'Developed an MVP for a web-based public speaking platform that integrates essential HCI elements, focusing on user interaction and feedback. It allows users to customize practice environments while offering real-time analysis of speech and body language to refine communication skills effectively.',
    technologies: ['TypeScript', 'Next JS', 'Material UI', 'Node.js', 'WebRTC', 'Mongo DB', 'Vercel', 'GitHub', 'Jest'],
    githubLink: 'https://github.com/Anjali0407-git/public_speaking_app',
    videoLink: 'https://drive.google.com/file/d/17Xe7-5CWcfmo-cxSsaf8vqJN7xqrTQ_R/view?usp=drive_link',
    paperLink: 'https://github.com/Anjali0407-git/public_speaking_app/blob/stable/Public_Speaking_Practice_using_VR.pdf'
  },
  {
    title: 'Weather Monitoring Safety Subsystem',
    year: '2024',
    image: 'weather.jpeg',
    description:
      'Architected and developed a weather monitoring safety subsystem in C++ that collects data from sensors and weather forecasts, processes this data, and yields commands to different systems, including emergency and UAV flight control systems and ground control station.',
    technologies: ['C++', 'OOPs', 'Object Oriented Software Design', 'GitHub', 'Azure'],
    githubLink: 'https://github.com/Anjali0407-git/weather_monitoring_safety_subsystem',
  },
  {
    title: 'Revive Gallery Marketplace',
    year: '2023',
    image: 'reviveGallery.png',
    description:
      'This platform is designed to create a secure trading environment by assigning trustworthiness scores to both buyers and sellers based on a variety of metrics, including product ratings, user reviews, and profile evaluations enhancing the transparency and trust on our marketplace. The platform also supports seamless chat functionality, robust buying and selling functionalities, making it an all-encompassing hub for e-commerce activities.',
    technologies: [ 'React JS', 'Node.js', 'Mongo DB', 'Google Firebase', 'CircleCI', 'Docker', 'Jest', 'GitHub', 'Jira', 'AWS'],
    githubLink: 'https://github.com/Anjali0407-git/Revive_Gallery',
    videoLink:
      'https://drive.google.com/file/d/1fy4YsfHXxNU0o3r7zFYT53wV3EGxzWIR/view',
  },
  {
    title: 'Predicting Smoking and Drinking Habits',
    year: '2023',
    image: 'machine_learning.jpg',
    description:
      'The project involved using machine learning techniques to predict smoking and drinking behaviors from body signal data. Methods were developed to classify and group data to identify patterns related to lifestyle choices. Detailed data analysis was conducted, and various visualizations such as correlation matrices, scatter plots, histograms, and density plots were created to support the findings.',
    technologies: ['Python', 'Machine Learning', 'AWS'],
    paperLink: 'https://drive.google.com/file/d/1EcPRb3upkfj6UGA5D5FYhMa_BmdQY6Mf/view'
  },
  {
    title: 'Reversi Board Game',
    year: '2023',
    image: 'reversi-game.png',
    description:
      'Developed a Reversi board game using Java Swing, structured with MVC architecture to segregate the game’s logic, user interactions, and visual display. This game can be played between a human player and a computer opponent. I programmed the automatic moves to be strategic, allowing the AIs decision-making to be both effective and sophisticated, deepening the games challenge and engagement.',
    technologies: ['Java', 'Swing', 'JUnit', 'MVC', 'OOPs', 'Gradle', 'GitHub', 'Azure'],
    githubLink: 'https://github.com/Anjali0407-git/ReversiGame',
  }
];

const AcademicProjects: React.FC<{ isSidebarOpen: boolean; }> = ({ isSidebarOpen }) => {
  const navigate = useNavigate();
  const theme = useTheme<Theme>(); // Get the theme and ensure it is of type Theme
  const isLargerThanSmall = useMediaQuery(theme.breakpoints.up('sm'));
  const isLargerThanLarge = useMediaQuery(theme.breakpoints.up('lg'));
  const isLargerThanMedium = useMediaQuery(theme.breakpoints.up('md'));
  const [hoverStates, setHoverStates] = useState(new Array(academicProjects.length).fill(false));
  const [selectedProject, setSelectedProject] = useState<number | null>(null);

  // Function to handle mouse enter
  const handleMouseEnter = (index: number): void => {
    const updatedHoverStates = hoverStates.map((_, i) => i === index);
    setHoverStates(updatedHoverStates);
    setSelectedProject(null);
    // setSelectedProject(selectedProject === index ? index : null);
  };

  // Function to handle mouse leave
  const handleMouseLeave = (index: number): void => {
    const updatedHoverStates = [...hoverStates];
    updatedHoverStates[index] = false;
    setHoverStates(updatedHoverStates);
    setSelectedProject(null);
    // setSelectedProject(selectedProject === index ? null : index);
  };

  const toggleDescription = (index: number): void => {
    setSelectedProject(selectedProject === index ? null : index);
    // const updatedHoverStates = [...hoverStates];
    // updatedHoverStates[index] = false;
    // setHoverStates(updatedHoverStates);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  return (
    <Box
      sx={{
        // p: isLargerThanSmall ? 6 : 2,
        // pl: isLargerThanSmall ? 10 : 3,
        // pr: isLargerThanSmall ? 8 : 2,
        // pt: isLargerThanSmall ? 8 : 6,
        p: 4,
        pb: 8,
        // pb: isLargerThanSmall ? (isLargerThanLarge ? 12 : 10) : 6,
        pl: isLargerThanSmall ? (isLargerThanLarge ? 12 : 8) : (isSidebarOpen? 3: 5),
        pr: isLargerThanSmall ? (isLargerThanLarge ? 12 : 8) : 3,
        backgroundColor: '#fffde770',
        width: '100%',
        overflowY: 'auto',
      }}
    >
      <Typography variant="h2" component="h2" sx={{ mb: 2, mt: 2, textAlign: 'center' }}>
        Academic Projects
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 5 }}>
        <div>
          <Button
            sx={buttonStyles}
            variant="contained" color="primary" endIcon={<WestSharpIcon/>}
            onClick={() => handleNavigation('/education')}>
            Education
          </Button>
        </div>
        <div>
          <Button 
            sx={buttonStyles}
            variant="contained" color="primary" startIcon={<EastSharpIcon/>}
            onClick={() => handleNavigation('/work-experience')}>
            {isLargerThanMedium ? 'Work Experience' : 'Work'}
          </Button>
          </div>
      </Box>
      <Grid container spacing={{ xs: 3, sm: 5, md: 6 }}>
        {academicProjects.map((project, index) => (
          <Grid
            // onMouseEnter={() => handleMouseEnter(index)}
            // onClick={() => toggleDescription(index)}
            // onMouseLeave={() => handleMouseLeave(index)}
            size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 4 }}
            key={index}
            sx={{
              background: 'transparent',
              '&:active': { // Style for when the card is being actively touched
        transform: 'scale(0.98)', // Slightly scale down the card
      }
            }}
          >
            <Card
              onMouseEnter={() => handleMouseEnter(index)}
              onClick={() => toggleDescription(index)}
              onMouseLeave={() => handleMouseLeave(index)}
              sx={{
                backgroundColor: 'rgba(255, 165, 125, 0.7)',
                backdropFilter: 'opacity(50%)',
                webkitBackdropFilter: 'opacity(40%)',
              }}
            >
              <CardHeader
                // avatar={
                //   <Avatar
                //     src={'school-logo.png'} // Assuming a logo for the project/school
                //     alt="School"
                //     sx={{ width: 42, height: 42 }}
                //   />
                // }
                avatar={
                  <Avatar sx={{ backgroundColor: 'secondary.dark' }}>
                    <LaptopMacIcon />
                  </Avatar>
                }
                title={project.title}
                subheader={`Developed in ${project.year}`}
                titleTypographyProps={{
                  variant: 'h6',
                  component: 'div',
                  sx: { fontWeight: 'bold' },
                }}
              />
              <CardMedia
                component="img"
                image={project.image}
                alt={`${project.title} Project`}
                sx={{
                  objectFit: 'fill',
                  minHeight: '13vw',
                }}
                
              />
              {(selectedProject === index || hoverStates[index]===true) && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 2,
                      bgcolor: 'rgba(0, 0, 0, 0.7)', // Semi-transparent black background for readability
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="white"
                      sx={{ textAlign: 'justify' }}
                    >
                      {project.description}
                    </Typography>
                  </Box>
                )}
              <CardContent>
                <Typography
                  variant="body1"
                  sx={{ mt: 2, mb: 1, fontWeight: '700' }}
                >
                  Technologies used:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {project.technologies.map(tech => (
                    <Box
                      key={tech}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 1,
                        py: 0.5,
                        bgcolor: 'background.default',
                        // bgcolor: 'rgba(26, 104, 87, 0.5)',
                        borderRadius: 1,
                      }}
                    >
                      <SkillIcon sx={{ fontSize: 14, mr: 0.5 }} />
                      <Typography variant="caption">{tech}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>

              {/* Footer with GitHub and Video Icons */}
              <CardActions>
                {project.githubLink &&
                  <IconButton
                    aria-label="GitHub"
                    href={project.githubLink}
                    target="_blank"
                  >
                    <GitHubIcon sx={{ color: 'primary.main' }} />
                  </IconButton>
                }
                {project.videoLink && 
                  <IconButton
                  aria-label="Demo Video"
                  href={project.videoLink}
                  target="_blank"
                >
                  <PlayCircleOutlineIcon sx={{ color: 'primary.main' }} />
                </IconButton>
                }
                {/* {project.websiteLink && 
                  <IconButton
                  aria-label="Website"
                  href={project.websiteLink}
                  target="_blank"
                >
                  <PublicIcon sx={{ color: 'primary.main' }} />
                </IconButton>
                } */}
                {project.paperLink && 
                  <IconButton
                    aria-label="Paper"
                    href={project.paperLink}
                    target="_blank"
                  >
                    <DescriptionIcon sx={{ color: 'primary.main' }} />
                  </IconButton>
                }
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 10, mb: 2 }}>
        <div>
          <Button
            sx={buttonStyles}
            variant="contained" color="primary" endIcon={<WestSharpIcon/>}
            onClick={() => handleNavigation('/education')}>
            Education
          </Button>
        </div>
        {/* {isLargerThanMedium ?? 
          (<div>
          <Button
            sx={{ ...middlebuttonStyles(), cursor: 'default' }}
            variant="contained" color="primary">
            Academic Projects
          </Button>
        </div>)
        } */}
        <div>
          <Button 
            sx={buttonStyles}
            variant="contained" color="primary" startIcon={<EastSharpIcon/>}
            onClick={() => handleNavigation('/work-experience')}>
            {isLargerThanMedium ? 'Work Experience' : 'Work'}
          </Button>
          </div>
      </Box>
    </Box>
  );
};

export default AcademicProjects;
